import { useEffect, useRef, type ReactNode } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { LucideIcon } from 'lucide-react';

export type CorModulo = 'lilas' | 'ciano' | 'amarelo' | 'branco';

type Props = {
  titulo: string;
  Icone: LucideIcon;
  cor: CorModulo;
  /** Conteúdo ao lado do título (contadores, progresso). */
  extra?: ReactNode;
  /** Botões de ação no canto do cabeçalho. */
  acoes?: ReactNode;
  children: ReactNode;
};

/** Onde o clique é para editar, não para arrastar o módulo. */
const INTERATIVO = 'input, textarea, select, button, a, label, [contenteditable="true"], .nodrag';

/**
 * Impede que o arraste do módulo comece em campos, botões e links (o motor do quadro escuta o clique no próprio nó;
 * parar aqui, antes dele, deixa o resto do módulo arrastável).
 */
function useArrasteForaDosCampos() {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const elemento = ref.current;
    if (!elemento) return;
    const parar = (e: Event) => {
      if ((e.target as Element).closest(INTERATIVO)) e.stopPropagation();
    };
    elemento.addEventListener('mousedown', parar);
    elemento.addEventListener('touchstart', parar);
    return () => {
      elemento.removeEventListener('mousedown', parar);
      elemento.removeEventListener('touchstart', parar);
    };
  }, []);
  return ref;
}

/**
 * Moldura dos módulos da licitação (referência B): ícone em círculo, título e ações. O módulo arrasta por qualquer
 * lugar fora dos campos e botões, e a roda do mouse dá zoom no quadro mesmo com o ponteiro sobre ele.
 */
export function Modulo({ titulo, Icone, cor, extra, acoes, children }: Props) {
  const ref = useArrasteForaDosCampos();
  return (
    <section ref={ref} className={`modulo modulo--${cor}`} aria-label={titulo}>
      {/* Pontas invisíveis: post-its e setas ligados ao módulo inteiro. */}
      <Handle type="target" position={Position.Left} className="ponta-oculta" isConnectable={false} />
      <Handle type="source" position={Position.Right} className="ponta-oculta" isConnectable={false} />
      <header className="modulo__cabecalho">
        <span className="modulo__icone" aria-hidden>
          <Icone size={20} />
        </span>
        <h2 className="modulo__titulo">{titulo}</h2>
        {extra}
        {acoes && <div className="modulo__acoes nodrag">{acoes}</div>}
      </header>
      <div className="modulo__conteudo">{children}</div>
    </section>
  );
}
